import { CreateTxOptions, MsgExecuteContract } from '@terra-money/terra.js'
import { AssetInfo } from 'interfaces/contracts/edgeEtoken'
import {
  Asset,
  ExecuteMsg,
  MarketResponse,
  QueryMsg,
  AccountStatusResponse,
  MarketWhitelistResponse,
  MarketConfigResponse,
  ActiveMarketResponse,
  ConfigResponse,
} from 'interfaces/contracts/edgePool'
import { Decimal256 } from 'interfaces/contracts/edgeFactory'
import {
  extractAddrOrDenomFromInfo,
  getCoins,
  isNativeTokenInfo,
} from 'utils/contractParse'
import { BaseLcdService } from './base/baseLcdService'
import { contractAddresses } from 'constants/address'
import { defaultNetworkName } from 'constants/network'
import { cw20Service } from './cw20Service'

class PoolService extends BaseLcdService {
  private poolAddr: string
  private senderAddr: string
  private pilotGateway: string

  constructor() {
    super()
    this.poolAddr = contractAddresses[defaultNetworkName].pilotPool
    this.pilotGateway = contractAddresses[defaultNetworkName].pilotGateway
  }

  initAddress = (poolAddr: string, senderAddr: string) => {
    this.poolAddr = poolAddr
    this.senderAddr = senderAddr
  }

  get poolAddress() {
    return this.poolAddr
  }

  getConfig = async () => {
    const msg: QueryMsg = {
      config: {},
    }

    const response = await this.contractQuery<ConfigResponse>(
      this.poolAddr,
      msg,
    )

    return response
  }
  getMarketWhitelist = async (underlying: AssetInfo) => {
    const msg: QueryMsg = {
      market_whitelist: {
        underlying,
      },
    }
    const response = await this.contractQuery<MarketWhitelistResponse[]>(
      this.poolAddr,
      msg,
    )

    return response
  }

  getMarketConfig = async (underlying: AssetInfo) => {
    const msg: QueryMsg = {
      market_config: {
        underlying,
      },
    }
    const response = await this.contractQuery<MarketConfigResponse>(
      this.poolAddr,
      msg,
    )

    return response
  }

  getEtokenToUnderling = async (etoken: string) => {
    const msg: QueryMsg = {
      etoken_to_underlying: {
        etoken,
      },
    }
    const response = await this.contractQuery<AssetInfo>(this.poolAddr, msg)

    return response
  }

  getMarketList = async () => {
    const msg: QueryMsg = {
      market_lists: {},
    }
    const response = await this.contractQuery<MarketResponse[]>(
      this.poolAddr,
      msg,
    )

    return response
  }

  getActiveMarkets = async () => {
    const msg: QueryMsg = {
      active_markets: {
        address: this.senderAddr,
      },
    }
    const response = await this.contractQuery<ActiveMarketResponse[]>(
      this.poolAddr,
      msg,
    )

    return response
  }

  getAccountStatus = async () => {
    const msg: QueryMsg = {
      account_status: {
        address: this.senderAddr,
      },
    }

    const response = await this.contractQuery<AccountStatusResponse>(
      this.poolAddr,
      msg,
    )

    return response
  }

  getExchangeRate = async (etoken: AssetInfo) => {
    const msg: QueryMsg = {
      exchange_rate: {
        etoken,
      },
    }
    const response = await this.contractQuery<Decimal256>(this.poolAddr, msg)

    return response
  }

  registerMarket = (
    underlying: AssetInfo,
    liquidation_incentive: Decimal256,
    is_reserve_active: boolean,
    collateral_factor?: Decimal256,
  ): CreateTxOptions => {
    const msg: ExecuteMsg = {
      register_market: {
        underlying,
        liquidation_incentive,
        is_reserve_active,
        collateral_factor,
      },
    }

    return {
      msgs: [new MsgExecuteContract(this.senderAddr, this.pilotGateway, msg)],
    }
  }

  registerBorrowingAsset = (
    underlying: AssetInfo,
    borrow_factor: Decimal256,
  ): CreateTxOptions => {
    const msg: ExecuteMsg = {
      register_borrowing_asset: {
        underlying,
        borrow_factor,
      },
    }

    return {
      msgs: [new MsgExecuteContract(this.senderAddr, this.pilotGateway, msg)],
    }
  }

  borrow = (underlying: Asset, to?: string): CreateTxOptions => {
    const msg: ExecuteMsg = {
      borrow: {
        underlying,
        to,
      },
    }

    return {
      msgs: [new MsgExecuteContract(this.senderAddr, this.pilotGateway, msg)],
    }
  }

  repay = (underlying: Asset, owner?: string): CreateTxOptions => {
    const msg: ExecuteMsg = {
      repay: {
        underlying,
        owner,
      },
    }
    const coins = getCoins(underlying)
    const _increaseAllowance = isNativeTokenInfo(underlying.info)
      ? { msgs: [] }
      : cw20Service.increaseAllowance(
          this.senderAddr,
          extractAddrOrDenomFromInfo(underlying.info),
          this.poolAddr,
          underlying.amount,
        )

    return {
      msgs: [
        ..._increaseAllowance.msgs,
        new MsgExecuteContract(this.senderAddr, this.pilotGateway, msg, coins),
      ],
    }
  }

  liquidate = (
    owner: string,
    underlying: Asset,
    collateral: AssetInfo,
  ): CreateTxOptions => {
    const msg: ExecuteMsg = {
      liquidate: {
        owner,
        underlying,
        collateral,
      },
    }
    const coins = getCoins(underlying)

    return {
      msgs: [
        new MsgExecuteContract(this.senderAddr, this.pilotGateway, msg, coins),
      ],
    }
  }

  lock = (underlying: AssetInfo): CreateTxOptions => {
    const msg: ExecuteMsg = {
      lock: {
        underlying,
      },
    }

    return {
      msgs: [new MsgExecuteContract(this.senderAddr, this.pilotGateway, msg)],
      // fee
    }
  }

  unlock = (underlying: AssetInfo): CreateTxOptions => {
    const msg: ExecuteMsg = {
      unlock: {
        underlying,
      },
    }

    return {
      msgs: [new MsgExecuteContract(this.senderAddr, this.pilotGateway, msg)],
    }
  }

  deposit = (underlying: Asset, to?: string): CreateTxOptions => {
    const msg: ExecuteMsg = {
      deposit: {
        underlying,
        to,
      },
    }
    const coins = getCoins(underlying)
    const _increaseAllowance = isNativeTokenInfo(underlying.info)
      ? { msgs: [] }
      : cw20Service.increaseAllowance(
          this.senderAddr,
          extractAddrOrDenomFromInfo(underlying.info),
          this.poolAddr,
          underlying.amount,
        )

    return {
      msgs: [
        ..._increaseAllowance.msgs,
        new MsgExecuteContract(this.senderAddr, this.pilotGateway, msg, coins),
      ],
    }
  }

  depositAndLockFunction = (
    underlying: Asset,
    isLock: boolean,
    to?: string,
  ): CreateTxOptions => {
    const _deposit = this.deposit(underlying, to)
    const _lock = isLock
      ? this.lock(underlying.info)
      : this.unlock(underlying.info)

    return {
      msgs: [..._deposit.msgs, ..._lock.msgs],
    }
  }

  withdraw = (etokenAsset: Asset, to?: string): CreateTxOptions => {
    const msg: ExecuteMsg = {
      withdraw: {
        etoken_asset: etokenAsset,
        to,
      },
    }

    const _increaseAllowance = isNativeTokenInfo(etokenAsset.info)
      ? { msgs: [] }
      : cw20Service.increaseAllowance(
          this.senderAddr,
          extractAddrOrDenomFromInfo(etokenAsset.info),
          this.poolAddr,
          etokenAsset.amount,
        )

    return {
      msgs: [
        ..._increaseAllowance.msgs,
        new MsgExecuteContract(
          this.senderAddr,
          this.pilotGateway,
          msg as object,
        ),
      ],
    }
  }
}

export const poolService = new PoolService()
